import { IBusinessToolType } from 'constants/businessPlan';
import { getAllSectionInfos } from 'PDF/helpers/sections';
import { shouldRenderFinancialData } from 'PDF/helpers/shouldRenderFinancialData';
import {
  AboutCompanyKeys,
  BusinessOperationsKeys,
  FinancialPlanKeys,
  ImplementationPlanKeys,
  LegalFrameworkAndRisksKeys,
  ManagementAndOrganizationKeys,
  MarketAnalysisKeys,
  MarketingAndSalesKeys,
  ProductsAndServicesKeys,
  RaisingAndAllocatingFundsKeys,
  SectionsAvailability,
  SummaryKeys,
} from 'PDF/types/pdfSections';
import {
  getAboutCompanyChapterNames,
  getBusinessOperationsChapterNames,
  getFinancialPlanChapterNames,
  getLegalFrameworkAndRisksChapterNames,
  getManagementAndOrganizationChapterNames,
  getMarketingAnalysisChapterNames,
  getMarketingAndSalesChapterNames,
  getProductAndServicesChapterNames,
  getSummaryChapterNames,
} from 'PDF/types/pdfSections';

import { IOpeningBalanceSheets } from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/utils/form';

interface IContentValue {
  heading: string;
  pageNumber: number;
  id: string;
}

interface ISections {
  [key: string]: IContentValue[];
}

export enum PrivateSectionsEnum {
  PRODUCT_AND_SERVICES = 'productAndServices',
  MARKET_ANALYSIS = 'marketAnalysis',
  BUSINESS_OPERATIONS = 'businessOperations',
  MANAGEMENT_AND_ORGANIZATION = 'managementAndOrganization',
  LEGAL_FRAMEWORK_AND_RISKS = 'legalFrameworkAndRisks',
  IMPLEMENTATION_PLAN = 'implementationPlan',
  FINANCIAL_PLAN = 'financialPlan',
  MARKETING_AND_SALES = 'marketingAndSales',
  RAISING_AND_ALLOCATING_FUNDS = 'raisingAndAllocatingFunds',
}

export const getSubChapters = (sections: ISections, key: string) => {
  return sections?.[key]
    ?.map(({ heading, id }) => ({
      subChapterName: heading,
      subChapterPage: '',
      id,
    }))
    .filter(({ subChapterName }) => subChapterName);
};

export const getNextSectionIndex = () => {
  let index = 0;

  return () => {
    return ++index;
  };
};

const getSectionTableData = <T extends string>(
  filledKeys: T[],
  chapterIndex: number,
  chapterName: string,
  sectionNames: Record<T, string>,
) => {
  const getSubChapterIndex = getNextSectionIndex();

  return {
    chapterNumber: String(chapterIndex),
    chapterName: chapterName,
    subChapters: filledKeys.map((key) => {
      const subChapterIndex = getSubChapterIndex();

      return {
        subChapterName: `${chapterIndex}.${subChapterIndex}. ${sectionNames[key]}`,
        subChapterPage: '',
        id: key,
      };
    }),
  };
};

export const getTableData = (
  privateSections: { [key in PrivateSectionsEnum]?: boolean },
  businessPlanType: IBusinessToolType,
  sectionsAvailability: SectionsAvailability,
  isExamplePage: boolean,
  openingBalanceSheetData?: IOpeningBalanceSheets,
) => {
  const getChapterIndex = getNextSectionIndex();
  const allSectionInfos = getAllSectionInfos(businessPlanType, isExamplePage);

  const summaryFilledKeys = (Object.keys(sectionsAvailability.summary) as SummaryKeys[]).filter(
    (key) => !!sectionsAvailability.summary[key],
  );
  const aboutCompanyFilledKeys = (Object.keys(sectionsAvailability.aboutCompany) as AboutCompanyKeys[]).filter(
    (key) => !!sectionsAvailability.aboutCompany[key],
  );
  const productsAndServicesFilledKeys = (
    Object.keys(sectionsAvailability.productsAndServices) as ProductsAndServicesKeys[]
  ).filter((key) => !!sectionsAvailability.productsAndServices[key]);
  const marketAnalysisFilledKeys = (Object.keys(sectionsAvailability.marketAnalysis) as MarketAnalysisKeys[]).filter(
    (key) => !!sectionsAvailability.marketAnalysis[key],
  );
  const marketingAndSalesFilledKeys = (
    Object.keys(sectionsAvailability.marketingAndSales) as MarketingAndSalesKeys[]
  ).filter((key) => !!sectionsAvailability.marketingAndSales[key]);
  const businessOperationsFilledKeys = (
    Object.keys(sectionsAvailability.businessOperations) as BusinessOperationsKeys[]
  ).filter((key) => !!sectionsAvailability.businessOperations[key]);
  const managementAndOrganizationFilledKeys = (
    Object.keys(sectionsAvailability.managementAndOrganization) as ManagementAndOrganizationKeys[]
  ).filter((key) => !!sectionsAvailability.managementAndOrganization[key]);
  const implementationPlanFilledKeys = (
    Object.keys(sectionsAvailability.implementationPlan) as ImplementationPlanKeys[]
  ).filter((key) => !!sectionsAvailability.implementationPlan[key]);
  const raisingAndAllocatingFundsFilledKeys = (
    Object.keys(sectionsAvailability.raisingAndAllocatingFunds) as RaisingAndAllocatingFundsKeys[]
  ).filter((key) => !!sectionsAvailability.raisingAndAllocatingFunds[key]);
  const legalFrameworkAndRisksFilledKeys = (
    Object.keys(sectionsAvailability.legalFrameworkAndRisks) as LegalFrameworkAndRisksKeys[]
  ).filter((key) => !!sectionsAvailability.legalFrameworkAndRisks[key]);
  const financialPlanFilledKeys = (Object.keys(sectionsAvailability.financialPlan) as FinancialPlanKeys[]).filter(
    (key) => {
      if (key === 'projectedBalanceSheet' && openingBalanceSheetData) {
        return shouldRenderFinancialData(openingBalanceSheetData) && !!sectionsAvailability.financialPlan[key];
      }
      return !!sectionsAvailability.financialPlan[key];
    },
  );

  return [
    ...(summaryFilledKeys.length > 0
      ? [
          getSectionTableData<SummaryKeys>(
            summaryFilledKeys,
            getChapterIndex(),
            allSectionInfos.summary.sectionHeading,
            getSummaryChapterNames(isExamplePage),
          ),
        ]
      : []),
    ...(aboutCompanyFilledKeys.length > 0
      ? [
          getSectionTableData<AboutCompanyKeys>(
            aboutCompanyFilledKeys,
            getChapterIndex(),
            allSectionInfos.aboutCompany.sectionHeading,
            getAboutCompanyChapterNames(isExamplePage),
          ),
        ]
      : []),
    ...(privateSections.productAndServices && productsAndServicesFilledKeys.length > 0
      ? [
          getSectionTableData<ProductsAndServicesKeys>(
            productsAndServicesFilledKeys,
            getChapterIndex(),
            allSectionInfos.productAndServices.sectionHeading,
            getProductAndServicesChapterNames(isExamplePage),
          ),
        ]
      : []),
    ...(privateSections.marketAnalysis && marketAnalysisFilledKeys.length > 0
      ? [
          getSectionTableData<MarketAnalysisKeys>(
            marketAnalysisFilledKeys,
            getChapterIndex(),
            allSectionInfos.marketAnalysis.sectionHeading,
            getMarketingAnalysisChapterNames(isExamplePage),
          ),
        ]
      : []),
    ...(privateSections.marketingAndSales && marketingAndSalesFilledKeys.length > 0
      ? [
          getSectionTableData<MarketingAndSalesKeys>(
            marketingAndSalesFilledKeys,
            getChapterIndex(),
            allSectionInfos.marketingAndSales.sectionHeading,
            getMarketingAndSalesChapterNames(isExamplePage),
          ),
        ]
      : []),
    ...(privateSections.businessOperations && businessOperationsFilledKeys.length > 0
      ? [
          getSectionTableData<BusinessOperationsKeys>(
            businessOperationsFilledKeys,
            getChapterIndex(),
            allSectionInfos.businessOperations.sectionHeading,
            getBusinessOperationsChapterNames(isExamplePage),
          ),
        ]
      : []),
    ...(privateSections.managementAndOrganization && managementAndOrganizationFilledKeys.length > 0
      ? [
          getSectionTableData<ManagementAndOrganizationKeys>(
            managementAndOrganizationFilledKeys,
            getChapterIndex(),
            allSectionInfos.managementAndOrganization.sectionHeading,
            getManagementAndOrganizationChapterNames(isExamplePage),
          ),
        ]
      : []),
    ...(privateSections.implementationPlan && implementationPlanFilledKeys.length > 0
      ? [
          {
            chapterNumber: String(getChapterIndex()),
            chapterName: allSectionInfos.implementationPlan.sectionHeading,
            subChapters: [],
          },
        ]
      : []),
    ...(privateSections.raisingAndAllocatingFunds && raisingAndAllocatingFundsFilledKeys.length > 0
      ? [
          {
            chapterNumber: String(getChapterIndex()),
            chapterName: allSectionInfos.raisingAndAllocatingFunds.sectionHeading,
            subChapters: [],
          },
        ]
      : []),
    ...(privateSections.legalFrameworkAndRisks && legalFrameworkAndRisksFilledKeys.length > 0
      ? [
          getSectionTableData<LegalFrameworkAndRisksKeys>(
            legalFrameworkAndRisksFilledKeys,
            getChapterIndex(),
            allSectionInfos.legalFrameworkAndRisks.sectionHeading,
            getLegalFrameworkAndRisksChapterNames(isExamplePage),
          ),
        ]
      : []),
    ...(privateSections.financialPlan && financialPlanFilledKeys.length > 0
      ? [
          getSectionTableData<FinancialPlanKeys>(
            financialPlanFilledKeys,
            getChapterIndex(),
            allSectionInfos.financialPlan.sectionHeading,
            getFinancialPlanChapterNames(isExamplePage),
          ),
        ]
      : []),
  ];
};
